import { Box, Grid, IconButton, lighten, makeStyles, Table, TableBody, TableCell, TableHead, TableRow, Theme, Typography } from "@material-ui/core";
import { CloudDownload, GetApp, Sync, UnfoldLess, UnfoldMore } from "@material-ui/icons";
import { Button, DataGrid, Paper, PaperBorderType, SelectField } from "@roc/ui";
import { formatCurrency } from "@roc/ui/utils";
import { observer } from "mobx-react";
import { useEffect, useState } from "react";
import EditIcon from '@material-ui/icons/Edit';
import { useDocumentStore } from "@roc/feature-documents";
import { EditPayoffAdjustmentsDialog } from "./dialogComponents/editPayoffAdjustmentsDialog";
import { RecalculatePayoffConfirmDialog } from "./dialogComponents/recalculatePayoffConfirmDialog";
import { PayoffCardRow } from "./cardComponents/payoffCardRow";
import { PayoffCardColumn } from "./cardComponents/payoffCardColumn";
import { PayoffCardColumnHeaderText } from "./cardComponents/payoffCardColumnHeaderText";
import { PayoffCardCellText } from "./cardComponents/payoffCardCellText";
import { PayoffCardTotalCellText } from "./cardComponents/payoffCardTotalCellText";
import { PayoffCardTotalRow } from "./cardComponents/payoffCardTotalRow";
import { PayoffCardAccordionRow } from "./cardComponents/payoffCardAccordionRow";
import { isNil } from "@roc/feature-utils";

const useStyles = makeStyles((theme: Theme) => ({
  cellText: {
    fontWeight: "bold",
  },
  selectStyle: {
    '& .MuiOutlinedInput-input': {
      paddingTop: theme.spacing(0.75),
      paddingBottom: theme.spacing(0.75),
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: lighten(theme.palette.primary.main, 0.4),
    },
    '& .MuiInputBase-input': {
      color: theme.palette.primary.main,
    },
    '& .MuiSelect-icon': {
      color: theme.palette.primary.main
    }
  },
  table: {
    minWidth: 750,
    width: '100%',
    '& td': {
      height: '40px',
      padding: 0,
      border: '1px solid #e0e0e0',
    },
  },
  headerCell: {
    height: '40px',
    fontWeight: 'bold',
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1),
    backgroundColor: '#ededed',
    border: '1px solid #e0e0e0',
  },
  tableCell: {
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1),
    fontSize: '14px',
    whiteSpace: 'nowrap',
  },
}));

const DateColumn = observer(({ children = undefined }) => {
  return (
    <PayoffCardColumn width='10%' textAlign='left'>
      {children}
    </PayoffCardColumn>
  );
});

const UnpaidInterestColumn = observer(({ children = undefined }) => {
  return (
    <PayoffCardColumn width='25%' textAlign='right'>
      {children}
    </PayoffCardColumn>
  );
});

const LatePenaltyColumn = observer(({ children = undefined }) => {
  return (
    <PayoffCardColumn width='30%' textAlign='right'>
      {children}
    </PayoffCardColumn>
  );
});

const DefaultInterestColumn = observer(({ children = undefined }) => {
  return (
    <PayoffCardColumn width='20%' textAlign='right'>
      {children}
    </PayoffCardColumn>
  );
});

const MaturityDefaultColumn = observer(({ children = undefined }) => {
  return (
    <PayoffCardColumn width='15%' textAlign='right'>
      {children}
    </PayoffCardColumn>
  );
});

const PayoffCalculatorHeaderRow = observer(({ firstColumnName }) => {
  const classes = useStyles();

  return (
    <PayoffCardRow>
      <Box display="flex" justifyContent="right" style={{ width: '96%' }}>
        <DateColumn>
          <PayoffCardColumnHeaderText>{firstColumnName}</PayoffCardColumnHeaderText>
        </DateColumn>
        <UnpaidInterestColumn>
          <PayoffCardColumnHeaderText>Unpaid Interest</PayoffCardColumnHeaderText>
        </UnpaidInterestColumn>
        <LatePenaltyColumn>
          <PayoffCardColumnHeaderText>Late Penalty</PayoffCardColumnHeaderText>
        </LatePenaltyColumn>
        <DefaultInterestColumn>
          <PayoffCardColumnHeaderText>Default Interest</PayoffCardColumnHeaderText>
        </DefaultInterestColumn>
        <MaturityDefaultColumn>
          <PayoffCardColumnHeaderText>Maturity Default</PayoffCardColumnHeaderText>
        </MaturityDefaultColumn>
      </Box>
    </PayoffCardRow>
  );
});

const AccordionListDetailsRow = observer(({ label, value }) => {
  const classes = useStyles();

  return (
    <Box style={{ display: 'flex' }}>
      <Box style={{ width: '70%', textAlign: 'left' }}>
        <Typography variant="caption" className={classes.cellText}>{label}</Typography>
      </Box>
      <Box style={{ width: '30%', textAlign: 'right' }}>
        <Typography variant="caption" style={{ color: value?.includes('D') || value?.includes('L') ? 'red' : '' }}>{value}</Typography>
      </Box>
    </Box>);
});

const PayoffCalculatorAccordionListSummary = observer(({ row }) => {
  const classes = useStyles();

  return (
    <Box display="flex" justifyContent="left" style={{ width: '98%' }}>
      <DateColumn>
        <PayoffCardCellText>{row?.operationDate ?? '-'}</PayoffCardCellText>
      </DateColumn>
      <UnpaidInterestColumn>
        <PayoffCardCellText>{!isNil(row?.interestDue) ? formatCurrency(row?.interestDue) : '-'}</PayoffCardCellText>
      </UnpaidInterestColumn>
      <LatePenaltyColumn>
        <PayoffCardCellText>{!isNil(row?.latePenaltyDue) ? formatCurrency(row?.latePenaltyDue) : '-'}</PayoffCardCellText>
      </LatePenaltyColumn>
      <DefaultInterestColumn>
        <PayoffCardCellText>{!isNil(row?.defaultPenaltyDue) ? formatCurrency(row?.defaultPenaltyDue) : '-'}</PayoffCardCellText>
      </DefaultInterestColumn>
      <MaturityDefaultColumn>
        <PayoffCardCellText>{!isNil(row?.maturityPenaltyDue) ? formatCurrency(row?.maturityPenaltyDue) : '-'}</PayoffCardCellText>
      </MaturityDefaultColumn>
    </Box>
  );
});

const PayoffCalculatorAccordionListDetails = observer(({ row }) => {
  const classes = useStyles();

  return (
    <Box display="flex" justifyContent="left" style={{ width: '95%' }}>
      <DateColumn />
      <UnpaidInterestColumn >
        <AccordionListDetailsRow label='Int Accrued' value={!isNil(row?.rocInterestAccrued) ? formatCurrency(row?.rocInterestAccrued) : '-'} />
        <AccordionListDetailsRow label='Servicer Int Accrued' value={!isNil(row?.slsInterestAccrued) ? formatCurrency(row?.slsInterestAccrued) : '-'} />
        <AccordionListDetailsRow label='Diff Roc Vs Servicer' value={!isNil(row?.interestDiff) ? formatCurrency(row?.interestDiff) : '-'} />
        <AccordionListDetailsRow label='Interest Paid' value={!isNil(row?.interestReceived) ? formatCurrency(row?.interestReceived) : '-'} />
        {/* <AccordionListDetailsRow label='Servicer Int Additional Charges' value={!isNil(row?.slsInterestAdditionalCharges) ? formatCurrency(row?.slsInterestAdditionalCharges) : '-'} /> */}
        <AccordionListDetailsRow label='Interest Arrears Accrued Vs Paid' value={!isNil(row?.interestDue) ? formatCurrency(row?.interestDue) : '-'} />
      </UnpaidInterestColumn>
      <LatePenaltyColumn>
        <AccordionListDetailsRow label='Paid Date' value={row?.paidDateWithStatus ?? '-'} />
        <AccordionListDetailsRow label='Late Penalty' value={!isNil(row?.rocLatePenalty) ? formatCurrency(row?.rocLatePenalty) : '-'} />
        <AccordionListDetailsRow label='Servicer Late Penalty' value={!isNil(row?.slsLatePenalty) ? formatCurrency(row?.slsLatePenalty) : '-'} />
        <AccordionListDetailsRow label='Diff Roc Vs Servicer' value={!isNil(row?.latePenaltyDiff) ? formatCurrency(row?.latePenaltyDiff) : '-'} />
        <AccordionListDetailsRow label='Late Penalty Paid' value={!isNil(row?.latePenaltyReceived) ? formatCurrency(row?.latePenaltyReceived) : '-'} />
        {/* <AccordionListDetailsRow label='Servicer Penalty Additional Charges' value={!isNil(row?.slsPenaltyAdditionalCharges) ? formatCurrency(row?.slsPenaltyAdditionalCharges) : '-'} /> */}
        <AccordionListDetailsRow label='Late Penalty Arrears Accrued Vs Paid' value={!isNil(row?.latePenaltyDue) ? formatCurrency(row?.latePenaltyDue) : '-'} />
      </LatePenaltyColumn>
      <DefaultInterestColumn>
        <AccordionListDetailsRow label='Default Penalty' value={!isNil(row?.defaultPenalty) ? formatCurrency(row?.defaultPenalty) : '-'} />
        <AccordionListDetailsRow label='Default Penalty Interest' value={!isNil(row?.defaultInterest) ? formatCurrency(row?.defaultInterest) : '-'} />
      </DefaultInterestColumn>
      <MaturityDefaultColumn />
    </Box>
  );
});

const PayoffCalculatorAccordionAggregatedSummary = observer(({ }) => {
  const classes = useStyles();

  const {
    documentStore: {
      documentFormStore: { payoffCalculatorStore },
    },
  } = useDocumentStore();

  return (
    <Box display="flex" justifyContent="left" style={{ width: '98%' }}>
      <DateColumn>
        <PayoffCardCellText>Aggregated</PayoffCardCellText>
      </DateColumn>
      <UnpaidInterestColumn>
        <PayoffCardCellText>{!isNil(payoffCalculatorStore?.unpaidInterestAggregated) ? formatCurrency(payoffCalculatorStore?.unpaidInterestAggregated) : '-'}</PayoffCardCellText>
      </UnpaidInterestColumn>
      <LatePenaltyColumn>
        <PayoffCardCellText>{!isNil(payoffCalculatorStore?.payoffCalculatorData?.latePenalty) ? formatCurrency(payoffCalculatorStore?.payoffCalculatorData?.latePenalty) : '-'}</PayoffCardCellText>
      </LatePenaltyColumn>
      <DefaultInterestColumn>
        <PayoffCardCellText>{!isNil(payoffCalculatorStore?.payoffCalculatorData?.defaultPenalty) ? formatCurrency(payoffCalculatorStore?.payoffCalculatorData?.defaultPenalty) : '-'}</PayoffCardCellText>
      </DefaultInterestColumn>
      <MaturityDefaultColumn>
        <PayoffCardCellText>{!isNil(payoffCalculatorStore?.payoffCalculatorData?.maturityPenalty) ? formatCurrency(payoffCalculatorStore?.payoffCalculatorData?.maturityPenalty) : '-'}</PayoffCardCellText>
      </MaturityDefaultColumn>
    </Box>
  );
});

const usdAmount: any = params => {
  return params.value && formatCurrency(Number(params.value));
};

const columns = [
  {
    field: 'operationDate',
    headerName: 'Date',
    minWidth: 120,
    maxWidth: 120,
    pinned: 'left',
    lockPinned: true,
  },
  {
    field: 'rocInterestAccrued',
    headerName: 'Int Accrued',
    valueFormatter: usdAmount,
    minWidth: 120,
  },
  {
    field: 'slsInterestAccrued',
    headerName: 'Servicer Int Accrued',
    valueFormatter: usdAmount,
    minWidth: 180,
  },
  {
    field: 'interestDiff',
    headerName: 'Diff Roc Vs Servicer',
    valueFormatter: usdAmount,
    minWidth: 180,
  },
  {
    field: 'interestReceived',
    headerName: 'Interest Paid',
    valueFormatter: usdAmount,
    minWidth: 120,
  },
  // {
  //   field: 'slsInterestAdditionalCharges',
  //   headerName: 'Servicer Int Additional Charges',
  //   valueFormatter: usdAmount,
  //   minWidth: 220,
  // },
  {
    field: 'interestDue',
    headerName: 'Interest Arrears Accrued Vs Paid',
    valueFormatter: usdAmount,
    minWidth: 230,
  },
  {
    field: 'paidDateWithStatus',
    headerName: 'Paid Date',
    minWidth: 180,
  },
  {
    field: 'rocLatePenalty',
    headerName: 'Late Penalty',
    valueFormatter: usdAmount,
    minWidth: 120,
  },
  {
    field: 'slsLatePenalty',
    headerName: 'Servicer Late Penalty',
    valueFormatter: usdAmount,
    minWidth: 180,
  },
  {
    field: 'latePenaltyDiff',
    headerName: 'Diff Roc Vs Servicer',
    valueFormatter: usdAmount,
    minWidth: 180,
  },
  {
    field: 'latePenaltyReceived',
    headerName: 'Late Penalty Paid',
    valueFormatter: usdAmount,
    minWidth: 150,
  },
  // {
  //   field: 'slsPenaltyAdditionalCharges',
  //   headerName: 'Servicer Penalty Additional Charges',
  //   valueFormatter: usdAmount,
  //   minWidth: 220,
  // },
  {
    field: 'latePenaltyDue',
    headerName: 'Late Penalty Arrears Accrued Vs Paid',
    valueFormatter: usdAmount,
    minWidth: 260,
  },
  {
    field: 'defaultPenalty',
    headerName: 'Default Penalty',
    valueFormatter: usdAmount,
    minWidth: 150,
  },
  {
    field: 'defaultInterest',
    headerName: 'Default Penalty Interest',
    valueFormatter: usdAmount,
    minWidth: 180,
  },
  ,
  {
    field: 'maturityPenaltyDue',
    headerName: 'Maturity Default',
    valueFormatter: usdAmount,
    minWidth: 150,
  },
];

const PayoffCalculatorAccordionTable = observer(({ }) => {
  const classes = useStyles();

  const {
    documentStore: {
      documentFormStore: { payoffCalculatorStore },
    },
  } = useDocumentStore();

  return (
    <Box display="flex" justifyContent="left" style={{ width: '100%' }}>
      <DataGrid
        columns={columns}
        rows={payoffCalculatorStore?.payoffCalculatorData?.revenueData}
        domLayout='autoHeight'
      />
    </Box>
  );
});

const PayoffCalculatorAccordionCompactTable = observer(({ }) => {
  const classes = useStyles();

  const {
    documentStore: {
      documentFormStore: { payoffCalculatorStore },
    },
  } = useDocumentStore();

  return (
    <Box display="flex" justifyContent="left" style={{ width: '100%' }}>
      <Table className={classes.table}>
        <TableHead>
          <TableRow>
            {columns.map(({ headerName, ...props }) => (
              <TableCell key={headerName} className={classes.headerCell} width={props.minWidth}>
                {headerName}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {payoffCalculatorStore?.payoffCalculatorData?.revenueData?.map((row, index) => {
            return (
              <TableRow key={index}>
                {columns.map(column => {
                  if (!isNil(column?.valueFormatter)) {
                    return (
                      <TableCell style={{ textAlign: 'right' }}>
                        <span className={classes.tableCell} >{!isNil(row[column?.field]) ? formatCurrency(row[column?.field]) : '-'}</span>
                      </TableCell>
                    )
                  } else {
                    return (
                      <TableCell style={{ textAlign: 'left' }}>
                        <span className={classes.tableCell} >{row[column?.field] ?? '-'}</span>
                      </TableCell>
                    )
                  }
                })}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </Box>
  );
});

const PayoffCalculatorAdjustmentsRow = observer(({ }) => {
  const classes = useStyles();

  const {
    documentStore: {
      documentFormStore: { payoffCalculatorStore },
    },
  } = useDocumentStore();

  return (
    <>
      <PayoffCardRow>
        <Box display="flex" justifyContent="right" style={{ width: '95%' }}>
          <DateColumn>
            <PayoffCardTotalCellText>Adjustment</PayoffCardTotalCellText>
          </DateColumn>
          <UnpaidInterestColumn>
            <PayoffCardCellText>{!isNil(payoffCalculatorStore?.payoffCalculatorData?.revenueAdjustment?.interestDue) ? formatCurrency(payoffCalculatorStore?.payoffCalculatorData?.revenueAdjustment?.interestDue) : '-'}</PayoffCardCellText>
          </UnpaidInterestColumn>
          <LatePenaltyColumn>
            <PayoffCardCellText>{!isNil(payoffCalculatorStore?.payoffCalculatorData?.revenueAdjustment?.latePenaltyDue) ? formatCurrency(payoffCalculatorStore?.payoffCalculatorData?.revenueAdjustment?.latePenaltyDue) : '-'}</PayoffCardCellText>
          </LatePenaltyColumn>
          <DefaultInterestColumn>
            <PayoffCardCellText>{!isNil(payoffCalculatorStore?.payoffCalculatorData?.revenueAdjustment?.defaultPenaltyDue) ? formatCurrency(payoffCalculatorStore?.payoffCalculatorData?.revenueAdjustment?.defaultPenaltyDue) : '-'}</PayoffCardCellText>
          </DefaultInterestColumn>
          <MaturityDefaultColumn>
            <PayoffCardCellText>{!isNil(payoffCalculatorStore?.payoffCalculatorData?.revenueAdjustment?.maturityPenaltyDue) ? formatCurrency(payoffCalculatorStore?.payoffCalculatorData?.revenueAdjustment?.maturityPenaltyDue) : '-'}</PayoffCardCellText>
          </MaturityDefaultColumn>
        </Box>
        <Box display="flex" justifyContent="right" style={{ width: '5%' }}>
          <IconButton onClick={() => payoffCalculatorStore.setIsEditPayoffAdjustmentsDialogOpen(true)} style={{ padding: '0' }}>
            <EditIcon />
          </IconButton>
        </Box>
      </PayoffCardRow>
      <EditPayoffAdjustmentsDialog />
    </>
  );
});

const PayoffCalculatorTotalRow = observer(({ }) => {
  const classes = useStyles();

  const {
    documentStore: {
      documentFormStore: { payoffCalculatorStore },
    },
  } = useDocumentStore();

  return (
    <PayoffCardTotalRow>
      <Box display="flex" justifyContent="right" style={{ width: '95%' }}>
        <DateColumn>
          <PayoffCardTotalCellText>Total</PayoffCardTotalCellText>
        </DateColumn>
        <UnpaidInterestColumn>
          <PayoffCardTotalCellText>{formatCurrency(payoffCalculatorStore.unpaidInterestTotal)}</PayoffCardTotalCellText>
        </UnpaidInterestColumn>
        <LatePenaltyColumn>
          <PayoffCardTotalCellText>{formatCurrency(payoffCalculatorStore.latePenaltyTotal)}</PayoffCardTotalCellText>
        </LatePenaltyColumn>
        <DefaultInterestColumn>
          <PayoffCardTotalCellText>{formatCurrency(payoffCalculatorStore.defaultInterestTotal)}</PayoffCardTotalCellText>
        </DefaultInterestColumn>
        <MaturityDefaultColumn>
          <PayoffCardTotalCellText>{formatCurrency(payoffCalculatorStore.maturityDefaultTotal)}</PayoffCardTotalCellText>
        </MaturityDefaultColumn>
      </Box>
    </PayoffCardTotalRow>
  );
});

export const PayoffCalculatorCard = observer(({ }) => {
  const classes = useStyles();

  const {
    documentStore: {
      documentFormStore: { payoffCalculatorStore },
    },
  } = useDocumentStore();

  const [expandAll, setExpandAll] = useState(0);
  const [collapseAll, setCollapseAll] = useState(0);

  return (
    <Paper titleComponent={
      <Box display="flex" p={2}>
        <Grid container justifyContent="space-between">
          <Grid item>
            <Typography variant="h5">
              Calculator
            </Typography>
          </Grid>
          <Grid item>
            <Grid container justifyContent="flex-end" spacing={1}>
              <Grid item>
                <Button
                  testId="expandAll"
                  onClick={() => {
                    setExpandAll(x => x + 1);
                  }}
                  variant="outlined"
                  size="small"
                  color="primary"
                  startIcon={<UnfoldMore />}
                >
                  Expand All
                </Button>
              </Grid>
              <Grid item>
                <Button
                  testId="collapseAll"
                  onClick={() => {
                    setCollapseAll(x => x + 1);
                  }}
                  variant="outlined"
                  size="small"
                  color="primary"
                  startIcon={<UnfoldLess />}
                >
                  Collapse All
                </Button>
              </Grid>
              <Grid item>
                <Button
                  testId="downloadPdf"
                  variant="outlined"
                  size="small"
                  color="primary"
                  onClick={() => payoffCalculatorStore.downloadCalculatorPDF()}
                  startIcon={<CloudDownload />}
                >
                  Download PDF
                </Button>
              </Grid>
              <Grid item>
                <Button
                  testId="downloadCalculatorExcel"
                  variant="outlined"
                  size="small"
                  color="primary"
                  onClick={() => payoffCalculatorStore.downloadPayoffExcel('calculator')}
                  startIcon={<GetApp />}
                >
                  Download Excel
                </Button>
              </Grid>
              <Grid item>
                <Button
                  testId="reCalculate"
                  variant="outlined"
                  size="small"
                  color="primary"
                  onClick={() => payoffCalculatorStore.setIsConfirmReCalculateDialogOpen(true)}
                  startIcon={<Sync />}
                  disabled={payoffCalculatorStore?.isPrePayoffCalculator}
                >
                  Re-calculate
                </Button>
              </Grid>
              <RecalculatePayoffConfirmDialog />
              <Grid item>
                <SelectField
                  id='selectedView'
                  value={payoffCalculatorStore?.selectedView}
                  options={[
                    { label: 'List Details View', value: 'listDetailsView' },
                    { label: 'Table View', value: 'tableView' },
                    { label: 'Compact Table View', value: 'compactTableView' }
                  ]}
                  onChange={(value) => payoffCalculatorStore.setSelectedView(value)}
                  variant={'outlined'}
                  testId={'selectedView'}
                  className={classes.selectStyle}
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Box>
    } borderType={PaperBorderType.SOLID_BORDER}>
      <Box style={{ padding: '0px 16px 16px' }} >
        <PayoffCalculatorHeaderRow firstColumnName={payoffCalculatorStore?.selectedView === 'listDetailsView' ? 'Date' : 'Name'} />
        {payoffCalculatorStore?.payoffCalculatorData?.revenueData?.length > 0 ?
          <>
            {payoffCalculatorStore?.selectedView === 'listDetailsView' &&
              payoffCalculatorStore?.payoffCalculatorData?.revenueData?.map(row => (
                <PayoffCardAccordionRow
                  isOpen={false}
                  expandAll={expandAll}
                  collapseAll={collapseAll}
                >
                  <PayoffCalculatorAccordionListSummary row={row} />
                  <PayoffCalculatorAccordionListDetails row={row} />
                </PayoffCardAccordionRow>
              ))
            }
            {payoffCalculatorStore?.selectedView === 'tableView' &&
              <PayoffCardAccordionRow
                isOpen={false}
                expandAll={expandAll}
                collapseAll={collapseAll}
              >
                <PayoffCalculatorAccordionAggregatedSummary />
                <PayoffCalculatorAccordionTable />
              </PayoffCardAccordionRow>
            }
            {payoffCalculatorStore?.selectedView === 'compactTableView' &&
              <PayoffCardAccordionRow
                isOpen={false}
                expandAll={expandAll}
                collapseAll={collapseAll}
              >
                <PayoffCalculatorAccordionAggregatedSummary />
                <PayoffCalculatorAccordionCompactTable />
              </PayoffCardAccordionRow>
            }
          </>
          :
          <PayoffCardRow>
            <DateColumn>
              <PayoffCardCellText>No Records.</PayoffCardCellText>
            </DateColumn>
          </PayoffCardRow>
        }
        <PayoffCalculatorAdjustmentsRow />
        <PayoffCalculatorTotalRow />
      </Box>
    </Paper>
  );
});